/* eslint-disable no-secrets/no-secrets */

import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import {
    CONSTANTS,
    WMICUserAccessUtil,
    WMICDriverUtil,
    WMICRichTextUtil,
    WMICVariousUtil,
    FlowStepId,
    WMICValidationUtil,
    useCanJumpToWizardStepDispatch,
} from 'wmic-pe-portals-utils-js';
import { WMICRequiredText } from 'wmic-pe-components-platform-react';
import { useWizardModals, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { WMICDriverDetailView, useDriverActions } from 'wmic-pe-capability-gateway-common-pa-react';
import { messages as quoteAndBindCommonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';

import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';

import messages from './WMICDriversDetailsPage.messages';
import metadata from './WMICDriversDetailsPage.metadata.json5';
import { TranslatorContext } from '@jutro/legacy/locale';

function WMICDriversDetailsPage(props) {
    const { wizardData: submissionVM, updateWizardData } = props;
    const translator = useContext(TranslatorContext);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader, authUserData } = useAuthentication();

    const [showErrors, setShowErrors] = useState(false);
    const {
        initDriverData,
        onCreateDriver,
        onCreateDriverWithModal,
        onSaveDriver,
        onSaveDriverMiddleware,
        onDeleteDriver,
        renderAddDriverButton,
    } = useDriverActions({ jobVM: submissionVM, updateWizardData, setShowErrors });

    const { setWizardLoading } = useWizardModals();
    const { wizardStepDispatch } = useCanJumpToWizardStepDispatch();

    const { onValidate, initialValidation, isComponentValid, registerInitialComponentValidation } =
        useValidation('paDriversDetailsPage');

    const isBMSi = _.get(submissionVM, 'isFromBMS_WMIC.value');

    useEffect(() => {
        submissionVM.value.flowStepId_WMIC = FlowStepId.PA_DRIVERS;
    }, [submissionVM.value]);

    useEffect(() => {
        registerInitialComponentValidation(
            () =>
                _.get(submissionVM, RISK_PATHS.DRIVERS_PATH).value.length > 0 &&
                !WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.PA_DRIVERS)
        );

        initDriverData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canEditSubmission = useMemo(
        () => WMICUserAccessUtil.canEditSubmission(authUserData.permission_Ext),
        [authUserData.permission_Ext]
    );

    const onCancelDriver = () => {
        _.set(submissionVM, 'isEditingPage.value', false);
        updateWizardData(submissionVM);
        wizardStepDispatch({ type: CONSTANTS.WIZARD_ACTION_TYPES.RESET, vm: submissionVM });
    };

    const onNext = useCallback(async () => {
        if (!isComponentValid || !WMICDriverUtil.presentDriverList(submissionVM)) {
            setShowErrors(true);

            return false;
        }

        if (canEditSubmission) {
            try {
                setWizardLoading(true);

                submissionVM.value = await LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);

                updateWizardData(submissionVM);

                if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.PA_DRIVERS)) {
                    WMICVariousUtil.scrollToTop();
                    return false;
                }

                return submissionVM;
            } finally {
                setWizardLoading(false);
            }
        }

        return false;
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentValid, canEditSubmission, setWizardLoading, LoadSaveService, submissionVM, authHeader]);

    const overrideProps = {
        // TODO: set visibility to !policyChange and usCanadaYearsLicensedTypeKey === undefined
        reenterMsgContainer: {
            visible: false,
        },
        reenterMsg: {
            content: WMICRichTextUtil.translateRichText(translator(messages.reenterMsg, '')),
        },
        atLeastOneMsgContainer: {
            visible: _.isEmpty(_.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, [])),
        },
        atLeastOneMsg: {
            content: WMICRichTextUtil.translateRichText(translator(messages.atLeastOne)),
        },
        driverListView: {
            VMList: _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.children`, []),
            clickable: true,
            startOpen: true,
            readOnly: !canEditSubmission,
            VMData: [
                {
                    headerText: translator(messages.driverName),
                    path: 'displayName',
                },
                {
                    headerText: translator(messages.licenseNumber),
                    path: 'licenseNumber',
                },
                {
                    headerText: translator(messages.licenseProvince),
                    path: 'licenseState',
                    getData: (row, path) => _.get(row.value, path) || translator(messages.licenseProvinceOther),
                },
            ],
            toCreate: isBMSi ? onCreateDriverWithModal : onCreateDriver,
            onSave: onSaveDriver,
            onSaveMiddleware: onSaveDriverMiddleware,
            onDelete: onDeleteDriver,
            onCancel: onCancelDriver,
            toUndoCreate: () => {
                const drivers = _.get(submissionVM.value, RISK_PATHS.DRIVERS_PATH);

                drivers.splice(drivers.length - 1, 1);
                _.set(submissionVM.value, RISK_PATHS.DRIVERS_PATH, drivers);
                updateWizardData(submissionVM);
            },
            detailViewComponent: WMICDriverDetailView,
            onValidate,
            renderAddButton: renderAddDriverButton,
            detailViewComponentProps: {
                jobVM: submissionVM,
            },
        },
        requiredText: {
            visible: _.get(submissionVM, RISK_PATHS.DRIVERS_PATH).length > 0,
        },
    };

    const resolvers = {
        resolveComponentMap: {
            WMICRequiredText,
        },
    };

    return (
        <WMICWizardSubmissionPage
            onNext={onNext}
            skipWhen={initialValidation}
            disableNext={!isComponentValid}
            cancelLabel={translator(quoteAndBindCommonMessages.saveAndExit)}
            showRequired
            flowStepId={FlowStepId.PA_DRIVERS}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                onModelChange={updateWizardData}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                showErrors={showErrors}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICDriversDetailsPage.propTypes = wizardProps;

export default WMICDriversDetailsPage;
